import { ModalDialog } from 'components/ModalDialog';
import type {
    BimPriceLevelEnum,
    BimWorkOrderPriceData,
    ContractStatusEnum,
    ProjectStatusEnum,
    WorkOrderStatusEnum,
} from 'gql/graphql';
import { BimCalculateInflationEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import type { AnyObject } from 'types/AnyObject';
import { Loader, Tabs } from 'uikit';

import type { ColDef, RowClassRules } from 'ag-grid-community';
import * as R from 'ramda';
import type React from 'react';
import { useCallback, useMemo } from 'react';

import { BimModalTitleWithPriceLevel } from '../../BimCommonComponents/BimModalTitleWithPriceLevel';
import type { IBimWorkOrderRowDetails } from '../BimWorkOrderPriceTable/cellRenderers';

import { BimActsKc2List } from './BimActsKc2List';
import { BimWorkOrderActKc2PriceDetails } from './BimWorkOrderActKc2PriceDetails';
import { BimWorkOrderActKc2TotalDetails } from './BimWorkOrderActKc2TotalDetails';
import { useBimActsKc2, useBimActsKc2DetailsData } from './hooks';
import { ActTotalDetailsType } from './types';

import Styles from './BimWorkOrderActsKc2.module.css';

interface IBimWorkOrderActsKc2 {
    baseRow: AnyObject[];
    onClose: () => void;
    selectedRow: IBimWorkOrderRowDetails<BimWorkOrderPriceData>;
    defaultColDef?: ColDef;
    rowClassRules?: RowClassRules;
    styleClass?: string;
    workOrderStatus?: WorkOrderStatusEnum;
    contractStatus?: ContractStatusEnum;
    statusProject?: ProjectStatusEnum;
    isVersionPage?: boolean;
    orderRules?: IUseAccess['orderBim'];
    priceLevel: BimPriceLevelEnum | undefined;
}

const getLabel = R.prop('name');

const tabsItems = [
    { id: ActTotalDetailsType.Totals, name: 'Итоги акта' },
    { id: ActTotalDetailsType.Prices, name: 'Состав акта' },
];

export const BimWorkOrderActsKc2: React.FC<IBimWorkOrderActsKc2> = (props) => {
    const {
        selectedRow,
        onClose,
        baseRow,
        defaultColDef,
        styleClass,
        workOrderStatus,
        contractStatus,
        statusProject,
        isVersionPage,
        orderRules,
        priceLevel,
    } = props;

    const workOrderPriceId = selectedRow.data.id;

    const {
        data: actKC2data,
        isLoading: isActsDataLoading,
        refresh: refreshActsKc2Data,
    } = useBimActsKc2(workOrderPriceId, priceLevel, isVersionPage);

    const {
        isDetailsOpened,
        detailsTitle,
        checkedActId,
        isTotalsLoading,
        isPricesLoading,
        setCheckedActForDetails,
        closeDetails,
        refreshActsKc2TotalsTable,
        totalsData,
        pricesData,
        actDetailsType,
        setActDetailsType,
        localPriceLevel,
        hasDeflation,
    } = useBimActsKc2DetailsData(priceLevel);

    const modalTitle = detailsTitle || 'Список Актов КС-2';

    const closeModalHandler = useCallback(() => {
        if (detailsTitle) {
            closeDetails();
        } else {
            onClose();
        }
    }, [closeDetails, detailsTitle, onClose]);
    const topModalContainerPadding = isDetailsOpened ? 20 : 0;
    const bodyModalContainerHeight = isDetailsOpened ? '' : '500px';

    const makeRefreshTotalsData = useCallback(() => {
        refreshActsKc2TotalsTable();
        refreshActsKc2Data();
    }, [refreshActsKc2TotalsTable, refreshActsKc2Data]);

    const onTabChange = useCallback(
        ({ value }) => {
            setActDetailsType?.(value.id);
        },
        [setActDetailsType],
    );

    const isCommitting = useMemo(
        () => isTotalsLoading || isPricesLoading || isActsDataLoading,
        [isActsDataLoading, isPricesLoading, isTotalsLoading],
    );

    const tabValue = useMemo(() => tabsItems.find((i) => i.id === actDetailsType), [actDetailsType]);

    const { checkedActInflationCalcType, isCheckedActInactive, checkedActCalcMethod } = useMemo(() => {
        const checkedAct = actKC2data?.find((a) => a.id === checkedActId);

        const useInflationValue = checkedAct?.useInflationValue
            ? BimCalculateInflationEnum.DefinedInPrice
            : BimCalculateInflationEnum.SystemCalculated;

        const isInactiveValue = !!checkedAct?.isInactive;
        const calcMethodValue = checkedAct?.calcMethod;

        return {
            checkedActInflationCalcType: useInflationValue,
            checkedActCalcMethod: calcMethodValue,
            isCheckedActInactive: isInactiveValue,
        };
    }, [actKC2data, checkedActId]);

    return (
        <ModalDialog
            isOpen
            title={<BimModalTitleWithPriceLevel priceLevel={priceLevel} modalTitle={modalTitle} />}
            onClose={closeModalHandler}
            dockedToWindow="bottom"
            withTitleCloseButton
            isOnClickOutsideDisable
            withBottomButtons={false}
            style={{ width: '100vw' }}
            bodyMinHeight="500px"
            bodyMaxHeight={bodyModalContainerHeight}
            bodyOverflow="hidden"
        >
            <div style={{ padding: `${topModalContainerPadding}px 20px` }}>
                {!isDetailsOpened && (
                    <BimActsKc2List
                        baseRow={baseRow}
                        actKC2data={actKC2data}
                        workOrderPriceId={workOrderPriceId}
                        refreshActsKc2Data={refreshActsKc2Data}
                        setCheckedActForDetails={setCheckedActForDetails}
                        defaultColDef={defaultColDef}
                        styleClass={styleClass}
                        workOrderStatus={workOrderStatus}
                        contractStatus={contractStatus}
                    />
                )}
                {isDetailsOpened && (
                    <>
                        <Tabs
                            items={tabsItems}
                            getItemLabel={getLabel}
                            onChange={onTabChange}
                            value={tabValue}
                            style={{ marginBottom: 20 }}
                        />
                        {actDetailsType === ActTotalDetailsType.Totals && (
                            <BimWorkOrderActKc2TotalDetails
                                actKC2Id={checkedActId}
                                actKC2Details={totalsData}
                                styleClass={styleClass}
                                defaultColDef={defaultColDef}
                                onSuccess={makeRefreshTotalsData}
                                isLoading={isTotalsLoading}
                                contractStatus={contractStatus}
                                workOrderStatus={workOrderStatus}
                                statusProject={statusProject}
                                isActionDisabled={!orderRules?.canEdit}
                                inflationCalcType={checkedActInflationCalcType}
                                calcMethod={checkedActCalcMethod}
                                isReadOnly={isCheckedActInactive}
                                hasDeflation={!!hasDeflation}
                            />
                        )}
                        {actDetailsType === ActTotalDetailsType.Prices && localPriceLevel && (
                            <BimWorkOrderActKc2PriceDetails
                                pricesData={pricesData}
                                styleClass={styleClass}
                                defaultColDef={defaultColDef}
                            />
                        )}
                    </>
                )}
                {isCommitting && (
                    <div className={Styles.overlayLoader}>
                        <Loader />
                    </div>
                )}
            </div>
        </ModalDialog>
    );
};

BimWorkOrderActsKc2.displayName = 'BimWorkOrderActsKc2';
